// src/pages/financeiro/ContaBancoCad.js
import React, { useState, useEffect } from 'react';

import Input from '../../components/input/Input.js';
import { useContaBanco } from '../../contexts/financeiro/ContaBancoContext.js';
import ContaBancoSearch from './ContaBancoSearch'; // Similar to FormaPagSearch
import Select from '../../components/input/Select.js';
import { ContaBanco } from '../../models/financeiro/ContaBanco.ts';
import { useMessage } from '../../contexts/geral/MessageContext.js';
import { useYesNo } from '../../contexts/geral/YesNoContext.js';

import ModalContainer from '../../components/modalForm/ModalContainer';
import ModalHeader from '../../components/modalForm/ModalHeader';
import ModalBody from '../../components/modalForm/ModalBody';
import ModalFooter from '../../components/modalForm/ModalFooter';
import ModalActions, { ModalActionButton } from '../../components/modalForm/ModalActions';

const ContaBancoCad = ({ show, onHide, contaBanco: initialContaBanco }) => {
    const { contasBanco, addContaBanco, updateContaBanco, deleteContaBanco } = useContaBanco();
    const [contaBanco, setContaBanco] = useState(initialContaBanco || new ContaBanco());
    const [showSearchModal, setShowSearchModal] = useState(false);
    const {showMessage} = useMessage();
    const { showYesNo } = useYesNo();
    const [novoReg, setNovoReg] = useState(false);  

    useEffect(() => {
        if (!initialContaBanco) {
            setContaBanco(new ContaBanco());
        }
    }, [initialContaBanco]);

    const handleNovo = () => {
        setContaBanco(new ContaBanco());
        setNovoReg(true);        
    };

    const handleEditar = (id) => {
        if (id === 0) {
            showMessage("Selecione uma conta bancária para editar!");            
            return;
        }            
        setNovoReg(false);
    };

    const handleDelete = async () => {
        if (!contaBanco?.id) {
            showMessage("Selecione uma conta bancária para excluir!");            
        } else {
            const resposta = await showYesNo("Deseja excluir a conta bancária selecionada?");
            if (resposta) {
                await deleteContaBanco(contaBanco.id);
                handleNovo();
            }
        }
    };

    const handleSave = async () => {
        try {
            if (!contaBanco.descricaoConta) {
                showMessage("Descrição não preenchida!");                
                return;
            }

            if (!contaBanco.id) {
                await addContaBanco(contaBanco);
            } else {
                await updateContaBanco(contaBanco);
            }
            handleNovo();
        } catch (error) {
            console.error("Erro ao gravar dados: ", error);
        }
    };

    const handleExit = () => {
        const contaBancoSelecionada = contasBanco.find(f => f.id.toString() === contaBanco?.id?.toString());

        if (contaBancoSelecionada) {
            setContaBanco(contaBancoSelecionada);
            handleEditar(contaBanco?.id);
        } else {
            setContaBanco(new ContaBanco());
        }
    };

    const handleSelectContaBanco = (contaBancoSel) => {
        setContaBanco(contaBancoSel);
        setShowSearchModal(false);
        if (contaBancoSel) {            
            handleEditar(contaBancoSel.id);
        } else {
            setContaBanco(new ContaBanco());
        }
    };

    const TipoImpressaoBoletoEnum = {
        Banco: 1,
        Empresa: 2,
        BancoPreEmiteClienteComplementa: 3,
        BancoReemite: 4,
        BancoNaoReemite: 5,
        BancoEmitenteAberta: 7,
        BancoEmitenteAutoEnvolopavel: 8
    };

    const TipoImpressaoBoletoOptions = [
        { value: TipoImpressaoBoletoEnum.Banco, label: 'Banco' },
        { value: TipoImpressaoBoletoEnum.Empresa, label: 'Empresa' },
        { value: TipoImpressaoBoletoEnum.BancoPreEmiteClienteComplementa, label: 'Banco Pre-emite Cliente Completa' },
        { value: TipoImpressaoBoletoEnum.BancoReemite, label: 'Banco Reemite' },
        { value: TipoImpressaoBoletoEnum.BancoNaoReemite, label: 'Banco Não Reemite' },
        { value: TipoImpressaoBoletoEnum.BancoEmitenteAberta, label: 'Banco Emitente Aberta' },
        { value: TipoImpressaoBoletoEnum.BancoEmitenteAutoEnvolopavel, label: 'Banco Emitente Auto Envolvível' },
    ];

    const TipoCarteiraEnum = {
        CarteiraCobrancaSimples: 1,
        CarteiraCobrancaVinculada: 2,
        CarteiraCobrancaCaucionada: 3,
        CarteiraCobrancaDescontada: 4,
        CarteiraCobrancaVendor: 5,
        CarteiraCobrancaDebito: 6
    };

    const TipoCarteiraOptions = [
        { value: TipoCarteiraEnum.CarteiraCobrancaSimples, label: 'Carteira Cobranca Simples' },
        { value: TipoCarteiraEnum.CarteiraCobrancaVinculada, label: 'Carteira Cobranca Vinculada' },
        { value: TipoCarteiraEnum.CarteiraCobrancaCaucionada, label: 'Carteira Cobranca Caucionada' },
        { value: TipoCarteiraEnum.CarteiraCobrancaDescontada, label: 'Carteira Cobranca Descontada' },
        { value: TipoCarteiraEnum.CarteiraCobrancaVendor, label: 'Carteira Cobranca Vendor' },
        { value: TipoCarteiraEnum.CarteiraCobrancaDebito, label: 'Carteira Cobranca Débito' }
    ];

    if (!show) return null;

    return (
        <ModalContainer show={show} largura="600px" variant="cadastro" onHide={onHide}>

            <ModalHeader title={novoReg ? 'Nova Conta Bancária' : 'Alterar Conta Bancária' } onHide={onHide} />
            <ModalBody className="modal-body">
                <form>
                    <div className="input-group mb-3">
                        <Input
                            label="Código"
                            value={contaBanco.id || ''}
                            onChange={(e) => setContaBanco({ ...contaBanco, id: e.target.value })}
                            onBlur={handleExit}
                            disabled={!novoReg}
                            btnBuscar={() => setShowSearchModal(true)}
                        />
                        <Input
                            label="Descrição Conta"
                            value={contaBanco.descricaoConta || ''}
                            onChange={(e) => setContaBanco({ ...contaBanco, descricaoConta: e.target.value })}
                            size={300}
                            autoFocus={true}
                        />
                    </div>

                    <div className="input-group">
                        <Input
                            label="Nº Agência"
                            value={contaBanco.agencia || ''}
                            onChange={(e) => setContaBanco({ ...contaBanco, agencia: e.target.value })}
                            size={100}
                        />
                        <Input
                            label="Nº Conta"
                            value={contaBanco.conta || ''}
                            onChange={(e) => setContaBanco({ ...contaBanco, conta: e.target.value })}
                            size={100}
                        />
                        <Input
                            label="Digito Conta"
                            value={contaBanco.digitoConta || ''}
                            onChange={(e) => setContaBanco({ ...contaBanco, digitoConta: e.target.value })}
                            size={100}
                        />
                    </div>

                    <div className="input-group">
                        <Input
                            label="Carteira Padrão"
                            value={contaBanco.carteiraPadrao || ''}
                            onChange={(e) => setContaBanco({ ...contaBanco, carteiraPadrao: e.target.value })}
                            size={100}
                        />
                        <Select
                            label="Tipo Carteira Padrão"
                            value={contaBanco.tipoCarteiraPadrao || ''}
                            onChange={(e) => setContaBanco({ ...contaBanco, tipoCarteiraPadrao: e.target.value })}
                            options={TipoCarteiraOptions}
                            size={300}
                        />
                    </div>
                    <div className="input-group">
                        <Select
                            label="Tipo de Carteira"
                            value={contaBanco.tipoFormaCadastramento || ''}
                            onChange={(e) => setContaBanco({ ...contaBanco, tipoFormaCadastramento: e.target.value })}
                            options={[{ value: '1', label: 'Com Registro' }, { value: '2', label: 'Sem Registro' }, { value: '3', label: 'Débito Automático' }]}
                            size={200}
                        />
                        <Select
                            label="Tipo de Impressão"
                            value={contaBanco.tipoImpressaoBoleto || ''}
                            onChange={(e) => setContaBanco({ ...contaBanco, tipoImpressaoBoleto: e.target.value })}
                            options={TipoImpressaoBoletoOptions}
                            size={300}
                        />
                    </div>

                    <div className="input-group">
                        <Input
                            label="Operação Conta"
                            value={contaBanco.operacaoConta || ''}
                            onChange={(e) => setContaBanco({ ...contaBanco, operacaoConta: e.target.value })}
                            size={100}
                        />
                    </div>
                </form>
            </ModalBody>
            <ModalFooter className="modal-footer justify-content-end">
                <ModalActions >
                    <ModalActionButton className="btn btn-primary" onClick={handleSave} action="save">
                        Salvar
                    </ModalActionButton>
                    <ModalActionButton className="btn btn-danger" onClick={handleDelete} disabled={novoReg} action="delete">
                        Excluir
                    </ModalActionButton>
                    <ModalActionButton className="btn btn-secondary" onClick={handleNovo} disabled={novoReg} action="new">
                        Novo
                    </ModalActionButton>
                </ModalActions>
            </ModalFooter>

            <ContaBancoSearch
                show={showSearchModal}
                onHide={() => setShowSearchModal(false)}
                onSelectContaBanco={handleSelectContaBanco}
            />        

        </ModalContainer>
    );
};

export default ContaBancoCad;
